/* eslint-disable react/prop-types */
/**
 * Page : Procédures Internes
 * Contient deux onglets : Procédures internes (A) + Référentiel des exigences (B)
 */
import { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import AuthContext from "../../context/AuthContext";
import { fadeInUp, fadeInDown } from "../../utils/animations";
import { StatutChip, AnalyseChip, EcartBadge } from "../../utils/conformiteChips";
import { downloadCSV } from "../../utils/exportCSV";

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const WRITE_ROLES = ["ADMIN_INSTITUTION", "GESTIONNAIRE"];

const TAB_LABELS = [
  { label: "Procédures internes", icon: "description" },
  { label: "Référentiel des exigences", icon: "fact_check" },
  { label: "Tableau de bord", icon: "dashboard" },
];

function canWrite(user) {
  if (!user) return false;
  const allRoles = [user.profile?.role, ...(user.profile?.all_roles || [])];
  return WRITE_ROLES.some((r) => allRoles.includes(r));
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers visuels
// ─────────────────────────────────────────────────────────────────────────────

const WF_LABEL = {
  BROUILLON: "Brouillon",
  EN_REVISION: "En révision",
  APPROUVE: "Approuvé",
  PUBLIE: "Publié",
};

// ─────────────────────────────────────────────────────────────────────────────
// Rapport : exigences réglementaires par domaine (textes actifs, impression)
function buildExigencesHtml(exigences) {
  const now = new Date().toLocaleString("fr-FR");
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const grouped = {};
  exigences.forEach((e) => {
    const dom = e.domaine_nom || "Sans domaine";
    if (!grouped[dom]) grouped[dom] = [];
    grouped[dom].push(e);
  });

  const total = exigences.length;
  const nbDomaines = Object.keys(grouped).length;

  const sections = Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dom, items]) => {
      const rows = items
        .map((e) => {
          const txt = e.exigence || "";
          return `<tr>
          <td style="white-space:nowrap;color:#0A3475;font-weight:600">${esc(
            e.reference || "-"
          )}</td>
          <td>${esc(txt)}</td>
          <td style="font-size:10px;color:#666">${esc(e.texte_titre || "-")}</td>
        </tr>`;
        })
        .join("");
      const pl = items.length > 1 ? "s" : "";
      return `<div class="sec">
      <div class="dh"><span class="di">◈</span>${esc(dom)}<span class="dc">${
        items.length
      } exigence${pl}</span></div>
      <table>
        <thead><tr><th style="width:90px">Référence</th><th>Exigence</th><th style="width:180px">Texte réglementaire</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Exigences réglementaires par domaine</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;width:200px;height:200px;
    border-radius:50%;background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700;letter-spacing:.4px;line-height:1.2}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.8}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.13);border-radius:10px;
    padding:12px 18px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:28px;font-weight:700;line-height:1;color:#a5d6a7}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8}
  .kpi{flex:1;padding:14px 16px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:22px;font-weight:700;color:#0A3475}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:26px 0 14px;letter-spacing:.3px}
  .sec{margin-bottom:0;page-break-inside:avoid}
  .dh{display:flex;align-items:center;gap:6px;
    background:linear-gradient(90deg,#eef2fb,#f5f7ff);
    border-left:4px solid #0A3475;padding:8px 12px;
    font-weight:700;font-size:12px;color:#0A3475;margin-bottom:0;border-radius:0 4px 0 0}
  .di{font-size:15px}
  .dc{margin-left:auto;font-size:10px;color:#7a8eab;background:#fff;
    padding:2px 8px;border-radius:10px;border:1px solid #c8d8ee;font-weight:500}
  table{width:100%;border-collapse:collapse}
  th{background:#1a237e;color:#fff;padding:7px 10px;text-align:left;
    font-size:10px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:6px 10px;border-bottom:1px solid #eef0f5;vertical-align:top;font-size:11px}
  tr:hover td{background:#f8faff}
  .ftr{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  @media print{
    .no-print{display:none}
    body{font-size:11px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,th,.hdr-badge,.dh{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    .sec{page-break-inside:avoid}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<div class="hdr">
  <div class="hdr-eye">ConformIMF · Procédures internes</div>
  <h1>Exigences Réglementaires par Domaine</h1>
  <div class="hdr-sub">Textes actifs uniquement</div>
  <div class="hdr-date">Généré le ${esc(now)}</div>
  <div class="hdr-badge">
    <div class="big">${total}</div>
    <div class="small">exigences<br>répertoriées</div>
  </div>
</div>

<div class="kpi-strip">
  <div class="kpi"><div class="v">${total}</div><div class="l">Total exigences</div></div>
  <div class="kpi"><div class="v">${nbDomaines}</div><div class="l">Domaines couverts</div></div>
</div>

<div class="content">
  ${
    sections ||
    '<p style="color:#999;font-style:italic;padding:16px 0">Aucune exigence trouvée.</p>'
  }
</div>

<div class="ftr">
  <span>ConformIMF · Document de référence</span>
  <strong>${esc(now)}</strong>
</div>
</body>
</html>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Génération rapport HTML
// ─────────────────────────────────────────────────────────────────────────────

function buildRapportProceduressHtml(procs, ref, instNom) {
  const now = new Date().toLocaleString("fr-FR");
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const actifs = procs.filter((p) => p.statut === "ACTIF").length;
  const enValidation = procs.filter((p) => p.statut === "EN_VALIDATION").length;
  const archives = procs.filter((p) => p.statut === "ARCHIVE").length;
  const totalRef = ref.length;
  const ecarts = ref.filter((l) => l.ecart_procedure).length;
  const conformes = ref.filter((l) => l.procedure_id && !l.ecart_procedure).length;
  const nonConformes = totalRef - conformes;
  const taux = totalRef > 0 ? Math.round((conformes / totalRef) * 100) : 0;
  const tauxColor = taux >= 80 ? "#2e7d32" : taux >= 50 ? "#e65100" : "#c62828";
  const tauxBg = taux >= 80 ? "#e8f5e9" : taux >= 50 ? "#fff3e0" : "#ffebee";

  // ── Groupes par domaine (procédures) ───────────────────────────────────────
  const domainesProc = [...new Set(procs.map((p) => p.domaine?.nom || "Sans domaine"))];
  const domaineStats = domainesProc.map((dom) => {
    const ps = procs.filter((p) => (p.domaine?.nom || "Sans domaine") === dom);
    const actifsDom = ps.filter((p) => p.statut === "ACTIF").length;
    return { dom, total: ps.length, actifs: actifsDom };
  });

  // ── Lignes tableau procédures ───────────────────────────────────────────────
  const procRows = procs
    .map((p, idx) => {
      const lignesProc = ref.filter((l) => l.procedure_id === p.id);
      const nbEcarts = lignesProc.filter((l) => l.ecart_procedure).length;
      const nbOkProc = lignesProc.filter((l) => !l.ecart_procedure).length;
      const pctOk = lignesProc.length > 0 ? Math.round((nbOkProc / lignesProc.length) * 100) : 0;
      const d = p.date_adoption;
      const dateFmt = d ? new Date(d).toLocaleDateString("fr-FR") : "-";
      const stLabel =
        p.statut === "ACTIF" ? "Actif" : p.statut === "EN_VALIDATION" ? "En valid." : "Archivée";
      const stBg =
        p.statut === "ACTIF" ? "#e8f5e9" : p.statut === "EN_VALIDATION" ? "#fff3e0" : "#f5f5f5";
      const stColor =
        p.statut === "ACTIF" ? "#2e7d32" : p.statut === "EN_VALIDATION" ? "#e65100" : "#757575";
      const miniBarColor = nbEcarts > 0 ? "#e65100" : "#2e7d32";
      const miniBar =
        lignesProc.length > 0
          ? `<div style="background:#e0e0e0;height:4px;border-radius:2px;margin-top:3px"><div style="width:${pctOk}%;height:4px;border-radius:2px;background:${miniBarColor}"></div></div>`
          : "";
      const ecartsColor = nbEcarts > 0 ? "#c62828" : "#bdbdbd";
      const ecartsCell = nbEcarts > 0 ? `⚠ ${nbEcarts}` : "-";
      const coverageColor =
        pctOk >= 80 ? "#2e7d32" : pctOk >= 50 ? "#e65100" : pctOk > 0 ? "#c62828" : "#bdbdbd";
      return `<tr>
        <td style="color:#aaa;font-size:10px;text-align:center;width:28px">${idx + 1}</td>
        <td><span style="font-weight:600;color:#0A3475">${esc(p.titre)}</span></td>
        <td style="color:#5c6bc0;font-size:11px">${esc(p.domaine?.nom || "-")}</td>
        <td style="width:80px"><span style="background:${stBg};color:${stColor};padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;white-space:nowrap">${stLabel}</span></td>
        <td style="color:#666;font-size:11px;white-space:nowrap">${dateFmt}</td>
        <td style="text-align:center">
          <span style="font-weight:700;color:#186A81">${lignesProc.length}</span>
          <span style="color:#aaa;font-size:10px">/${totalRef}</span>
          ${miniBar}
        </td>
        <td style="text-align:center;font-weight:700;color:${ecartsColor}">${ecartsCell}</td>
        <td style="text-align:center;width:64px">
          <span style="font-weight:700;color:${coverageColor}">${pctOk}%</span>
        </td>
      </tr>`;
    })
    .join("");

  // ── Couverture par procédure (cartes) ─────────────────────────────────────
  const procStatsRows = procs
    .filter((p) => ref.some((l) => l.procedure_id === p.id))
    .map((p) => {
      const lignesProc = ref.filter((l) => l.procedure_id === p.id);
      const nbOk = lignesProc.filter((l) => !l.ecart_procedure).length;
      const nbEc = lignesProc.filter((l) => l.ecart_procedure).length;
      const pctOk = lignesProc.length > 0 ? Math.round((nbOk / lignesProc.length) * 100) : 0;
      const pctEc = lignesProc.length > 0 ? Math.round((nbEc / lignesProc.length) * 100) : 0;
      const cardColor = nbEc > 0 ? "#e65100" : "#2e7d32";
      const cardBg = nbEc > 0 ? "#fff8f0" : "#f0faf2";
      const pTitre = esc(p.titre);
      const pDom = esc(p.domaine?.nom || "");
      const exigPl = lignesProc.length > 1 ? "s" : "";
      const okPl = nbOk > 1 ? "s" : "";
      const nbEcPl = nbEc > 1 ? "s" : "";
      const ecartSpan =
        nbEc > 0
          ? `<span style="color:#c62828;font-weight:600">⚠ ${nbEc} écart${nbEcPl}</span>`
          : "";
      return `<div style="margin-bottom:14px;padding:14px 16px;background:${cardBg};border-radius:8px;border-left:4px solid ${cardColor};page-break-inside:avoid">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:8px">
          <div>
            <div style="font-weight:700;color:#0A3475;font-size:12px;margin-bottom:2px">${pTitre}</div>
            <div style="font-size:10px;color:#888">${pDom}</div>
          </div>
          <div style="text-align:right;flex-shrink:0">
            <div style="font-size:20px;font-weight:700;color:${cardColor};line-height:1">${pctOk}%</div>
            <div style="font-size:10px;color:#888">taux de conformité</div>
          </div>
        </div>
        <div style="display:flex;gap:0;background:#e0e0e0;height:8px;border-radius:4px;overflow:hidden;margin-bottom:8px">
          <div style="width:${pctOk}%;height:8px;background:#2e7d32"></div>
          <div style="width:${pctEc}%;height:8px;background:#e65100"></div>
        </div>
        <div style="display:flex;gap:20px;font-size:11px">
          <span style="color:#555">${lignesProc.length} exigence${exigPl} rattachée${exigPl}</span>
          <span style="color:#2e7d32;font-weight:600">✓ ${nbOk} conforme${okPl}</span>
          ${ecartSpan}
        </div>
      </div>`;
    })
    .join("");

  // ── Lignes référentiel ─────────────────────────────────────────────────────
  const refRows = ref
    .map((l, idx) => {
      const hasProc = !!l.procedure_id;
      const hasEcart = l.ecart_procedure;
      let badgeBg = "#e8f5e9";
      let badgeColor = "#2e7d32";
      let badgeTxt = "Conforme";
      if (!hasProc) {
        badgeBg = "#ffebee";
        badgeColor = "#c62828";
        badgeTxt = "Non couverte";
      } else if (hasEcart) {
        badgeBg = "#fff8e1";
        badgeColor = "#e65100";
        badgeTxt = "Écart";
      }
      const refLabel = l.texte_titre || "";
      const refCode = l.exigence?.reference || "";
      const lCode = esc(l.code || "-");
      const refLabelEsc = esc(refLabel);
      const refLabelHtml = refLabel
        ? `<div style="color:#5c6bc0;font-weight:600;font-size:10px;margin-bottom:2px">${refLabelEsc}</div>`
        : "";
      const refCodeHtml = refCode
        ? `<div style="color:#888;font-size:10px">[${esc(refCode)}]</div>`
        : "";
      const exigTxt = esc(l.exigence?.exigence || "");
      const exigenceTextHtml = l.exigence?.exigence
        ? `<div style="color:#444;margin-top:3px;font-size:11px;line-height:1.4">${exigTxt}</div>`
        : "";
      const obsStyle = l.observations ? "normal" : "italic";
      const obsEsc = esc(l.observations || "-");
      const trBg = hasEcart ? "background:#fffde7" : !hasProc ? "background:#fff8f8" : "";
      return `<tr style="${trBg}">
        <td style="color:#aaa;font-size:10px;text-align:center;width:28px">${idx + 1}</td>
        <td style="font-weight:700;color:#0A3475;font-size:11px;white-space:nowrap">${lCode}</td>
        <td style="font-size:11px">
          ${refLabelHtml}
          ${refCodeHtml}
          ${exigenceTextHtml}
        </td>
        <td style="font-size:11px;color:#333">${esc(l.procedure?.titre || "-")}</td>
        <td style="text-align:center;width:90px"><span style="background:${badgeBg};color:${badgeColor};padding:3px 9px;border-radius:10px;font-size:10px;font-weight:700;white-space:nowrap">${badgeTxt}</span></td>
        <td style="font-size:11px;color:#666;font-style:${obsStyle}">${obsEsc}</td>
      </tr>`;
    })
    .join("");

  // ── Statistiques par domaine (sidebar) ────────────────────────────────────
  const domainStatRows = domaineStats
    .map(({ dom, total, actifs: a }) => {
      const aPl = a > 1 ? "s" : "";
      return `
    <div style="display:flex;justify-content:space-between;align-items:center;
      padding:5px 0;border-bottom:1px solid #eee;font-size:11px">
      <span style="color:#444">${esc(dom)}</span>
      <span>
        <span style="background:#e8f5e9;color:#2e7d32;padding:1px 7px;border-radius:8px;font-weight:600;font-size:10px">${a} actif${aPl}</span>
        <span style="color:#aaa;font-size:10px;margin-left:4px">${total} total</span>
      </span>
    </div>`;
    })
    .join("");

  // ── Pré-calcul des snippets HTML pour éviter les ternaires inline ──────────
  const conformesPl = conformes > 1 ? "s" : "";
  const ecartsPlS = ecarts > 1 ? "s" : "";
  const ecartsSnippet =
    ecarts > 0
      ? `<span style="color:#c62828">${ecarts} écart${ecartsPlS} signalé${ecartsPlS}</span>`
      : '<span style="color:#2e7d32">Aucun écart</span>';
  const conformiteBannerText = `${conformes} exigence${conformesPl} conforme${conformesPl} sur ${totalRef} · ${ecartsSnippet}`;
  const enValidationKpi =
    enValidation > 0
      ? `<div class="kpi warn"><div class="v">${enValidation}</div><div class="l">En validation</div></div>`
      : "";
  const nonConformesKpiClass = nonConformes > 0 ? " err" : " dim";
  const refLignesPl = ref.length > 1 ? "s" : "";
  const procStatsContent =
    procStatsRows ||
    `<p style="color:#999;font-style:italic;padding:12px 0">Aucune procédure avec exigences rattachées.</p>`;
  const domaineGrid =
    domaineStats.length > 0
      ? `<h2>Synthèse par domaine</h2>
  <div class="dom-grid">
    ${domaineStats
      .map(({ dom, total, actifs: a }) => {
        const totalPl = total > 1 ? "s" : "";
        const aPl2 = a > 1 ? "s" : "";
        return `<div class="dom-card">
      <div style="font-weight:700;color:#0A3475;font-size:12px;margin-bottom:6px">${esc(dom)}</div>
      <div style="font-size:11px;color:#555">${total} procédure${totalPl} · <span style="color:#2e7d32;font-weight:600">${a} active${aPl2}</span></div>
    </div>`;
      })
      .join("")}
  </div>`
      : "";

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Rapport Procédures - ${esc(instNom)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;display:flex;gap:8px;align-items:center;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700;letter-spacing:.3px}
  .btn-p:hover{background:#186A81}

  /* HEADER */
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:36px 40px 28px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;
    width:220px;height:220px;border-radius:50%;
    background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eyebrow{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;
    opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:22px;font-weight:700;letter-spacing:.5px;line-height:1.2}
  .hdr-inst{margin-top:8px;font-size:13px;font-weight:600;opacity:.9}
  .hdr-date{margin-top:4px;font-size:11px;opacity:.65}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.12);border-radius:12px;
    padding:14px 20px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:32px;font-weight:700;line-height:1}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}

  /* COVERAGE BANNER */
  .cov-banner{display:flex;align-items:center;gap:0;background:#f0f4ff;
    border-bottom:1px solid #d0daf5;padding:16px 40px}
  .cov-label{flex:1;font-size:12px;color:#333}
  .cov-label strong{display:block;font-size:13px;color:#0A3475;margin-bottom:2px}
  .cov-bar-wrap{flex:2;margin:0 24px}
  .cov-bar{background:#e0e0e0;height:12px;border-radius:6px;overflow:hidden}
  .cov-fill{height:12px;border-radius:6px;background:${tauxColor}}
  .cov-pct{font-size:28px;font-weight:700;color:${tauxColor};min-width:64px;text-align:right}

  /* KPI STRIP */
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8}
  .kpi{flex:1;padding:14px 16px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:24px;font-weight:700}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .kpi.ok .v{color:#2e7d32}
  .kpi.warn .v{color:#e65100}
  .kpi.err .v{color:#c62828}
  .kpi.neu .v{color:#0A3475}
  .kpi.dim .v{color:#9e9e9e}

  /* CONTENT */
  .content{padding:24px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:28px 0 14px;letter-spacing:.3px}
  h2:first-child{margin-top:0}

  /* TABLES */
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{background:#1a237e;color:#fff;padding:8px 10px;text-align:left;font-size:10px;
    font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:8px 10px;border-bottom:1px solid #eef0f5;vertical-align:top}
  tr:nth-child(even) td{background:#fafbff}
  tr:hover td{background:#f0f4ff}

  /* DOMAIN SUMMARY */
  .dom-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:6px}
  .dom-card{background:#f8f9fa;border-radius:6px;padding:10px 14px;
    border-top:3px solid #5c6bc0}

  /* FOOTER */
  .footer{margin-top:40px;padding:16px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;align-items:center;
    font-size:10px;color:#bbb}
  .footer strong{color:#888}

  @media print{
    .no-print{display:none}
    body{font-size:11px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,.cov-banner,th,h2,.cov-fill,.hdr-badge{
      -webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    h2{page-break-after:avoid}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<!-- HEADER -->
<div class="hdr">
  <div class="hdr-eyebrow">${esc(instNom)} · Rapport officiel</div>
  <h1>RAPPORT DES PROCÉDURES INTERNES</h1>
  <div class="hdr-inst">${esc(instNom)}</div>
  <div class="hdr-date">Généré le ${esc(now)}</div>
  <div class="hdr-badge">
    <div class="big">${taux}%</div>
    <div class="small">Conformité<br>réglementaire</div>
  </div>
</div>

<!-- COVERAGE BANNER -->
<div class="cov-banner">
  <div class="cov-label">
    <strong>Taux de conformité réglementaire</strong>
    ${conformiteBannerText}
  </div>
  <div class="cov-bar-wrap">
    <div class="cov-bar"><div class="cov-fill" style="width:${taux}%"></div></div>
    <div style="display:flex;justify-content:space-between;font-size:10px;color:#aaa;margin-top:3px">
      <span>0%</span><span>50%</span><span>100%</span>
    </div>
  </div>
  <div class="cov-pct">${taux}%</div>
</div>

<!-- KPI STRIP -->
<div class="kpi-strip">
  <div class="kpi neu"><div class="v">${actifs}</div><div class="l">Procédures actives</div></div>
  ${enValidationKpi}
  <div class="kpi dim"><div class="v">${archives}</div><div class="l">Archivées</div></div>
  <div class="kpi neu"><div class="v">${totalRef}</div><div class="l">Exigences totales</div></div>
  <div class="kpi ok"><div class="v">${conformes}</div><div class="l">Exigences couvertes</div></div>
  <div class="kpi${nonConformesKpiClass}"><div class="v">${nonConformes}</div><div class="l">Exigences non couvertes</div></div>
</div>

<div class="content">
  <!-- SYNTHÈSE PAR DOMAINE -->
  ${domaineGrid}

  <!-- PROCÉDURES -->
  <h2>Procédures internes (${procs.length})</h2>
  ${
    procs.length === 0
      ? `<p style="color:#999;font-style:italic;padding:12px 0">Aucune procédure.</p>`
      : `<table>
    <thead>
      <tr>
        <th>#</th><th>Titre</th><th>Domaine</th><th>Statut</th>
        <th>Date adoption</th><th>Exigences liées</th><th>Écarts</th><th>Conformité</th>
      </tr>
    </thead>
    <tbody>${procRows}</tbody>
  </table>`
  }

  <!-- CONFORMITÉ PAR PROCÉDURE -->
  <h2>Conformité par procédure</h2>
  ${procStatsContent}

  <!-- RÉFÉRENTIEL -->
  <h2>Référentiel des exigences (${ref.length} ligne${refLignesPl})</h2>
  ${
    ref.length === 0
      ? `<p style="color:#999;font-style:italic;padding:12px 0">Aucune ligne dans le référentiel.</p>`
      : `<table>
    <thead>
      <tr>
        <th style="width:28px">#</th>
        <th style="width:72px">Code</th>
        <th>Exigence réglementaire</th>
        <th style="width:180px">Procédure liée</th>
        <th style="width:90px">Conformité</th>
        <th style="width:170px">Observations</th>
      </tr>
    </thead>
    <tbody>${refRows}</tbody>
  </table>`
  }
</div>

<div class="footer">
  <span>Document confidentiel - usage interne uniquement</span>
  <strong>${esc(instNom)} · ${esc(now)}</strong>
</div>
</body>
</html>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rapport : indicateurs de conformité d'une procédure (impression)
// ─────────────────────────────────────────────────────────────────────────────

function buildIndicateursProcedureHtml(proc, lignes, instNom) {
  const now = new Date().toLocaleString("fr-FR");
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const total = lignes.length;
  const nbOk = lignes.filter((l) => !l.ecart_procedure).length;
  const nbEcarts = lignes.filter((l) => l.ecart_procedure).length;
  const tauxConf = total > 0 ? Math.round((nbOk / total) * 100) : 0;
  const badgeColor = tauxConf >= 80 ? "#a5d6a7" : tauxConf >= 50 ? "#ffcc80" : "#ef9a9a";

  const stLabel =
    proc.statut === "ACTIF"
      ? "Actif"
      : proc.statut === "EN_VALIDATION"
      ? "En validation"
      : proc.statut === "BROUILLON"
      ? "Brouillon"
      : "Archivé";
  const dateAdoption = proc.date_adoption
    ? new Date(proc.date_adoption).toLocaleDateString("fr-FR")
    : null;

  const rows = lignes
    .map((l, idx) => {
      const badgeBg = l.ecart_procedure ? "#fdecea" : "#e8f5e9";
      const badgeTxtColor = l.ecart_procedure ? "#c62828" : "#2e7d32";
      const badgeTxt = l.ecart_procedure ? "Écart" : "Conforme";
      const refLabel = [l.texte_titre, l.exigence?.reference].filter(Boolean).join(" - ");
      const obsStyle = l.observations ? "normal" : "italic";
      const obsEsc = esc(l.observations || "-");
      return `<tr>
        <td style="color:#aaa;font-size:10px;text-align:center;width:28px">${idx + 1}</td>
        <td style="font-size:11px">
          <div>${esc(l.exigence?.exigence || "-")}</div>
          ${
            refLabel
              ? `<div style="color:#9e9e9e;font-size:10px;margin-top:2px">${esc(refLabel)}</div>`
              : ""
          }
        </td>
        <td style="text-align:center;width:90px"><span style="background:${badgeBg};color:${badgeTxtColor};padding:3px 9px;border-radius:10px;font-size:10px;font-weight:700;white-space:nowrap">${badgeTxt}</span></td>
        <td style="font-size:11px;color:#666;font-style:${obsStyle}">${obsEsc}</td>
      </tr>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Indicateurs - ${esc(proc.titre)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;width:200px;height:200px;
    border-radius:50%;background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700;letter-spacing:.4px;line-height:1.2}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.8}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.13);border-radius:10px;
    padding:12px 18px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:28px;font-weight:700;line-height:1;color:${badgeColor}}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8}
  .kpi{flex:1;padding:14px 16px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:22px;font-weight:700}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .kpi.neu .v{color:#0A3475}
  .kpi.ok .v{color:#2e7d32}
  .kpi.err .v{color:${nbEcarts > 0 ? "#c62828" : "#9e9e9e"}}
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:26px 0 14px;letter-spacing:.3px}
  h2:first-child{margin-top:0}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{background:#1a237e;color:#fff;padding:7px 10px;text-align:left;
    font-size:10px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:7px 10px;border-bottom:1px solid #eef0f5;vertical-align:top}
  tr:nth-child(even) td{background:#fafbff}
  .ftr{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  @media print{
    .no-print{display:none}
    body{font-size:11px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,th,.hdr-badge{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    h2{page-break-after:avoid}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<div class="hdr">
  <div class="hdr-eye">${esc(instNom)} · Indicateurs de conformité</div>
  <h1>${esc(proc.titre)}</h1>
  <div class="hdr-sub">${esc(proc.domaine?.nom || "-")} · ${stLabel}${
    dateAdoption ? ` · Adoptée le ${esc(dateAdoption)}` : ""
  }</div>
  <div class="hdr-date">Généré le ${esc(now)}</div>
  <div class="hdr-badge">
    <div class="big">${tauxConf}%</div>
    <div class="small">Taux de<br>conformité</div>
  </div>
</div>

<div class="kpi-strip">
  <div class="kpi neu"><div class="v">${total}</div><div class="l">Exigences liées</div></div>
  <div class="kpi ok"><div class="v">${nbOk}</div><div class="l">Exigences couvertes</div></div>
  <div class="kpi err"><div class="v">${nbEcarts}</div><div class="l">Exigences non couvertes</div></div>
</div>

<div class="content">
  <h2>Exigences réglementaires liées (${total})</h2>
  ${
    total === 0
      ? `<p style="color:#999;font-style:italic;padding:12px 0">Aucune exigence liée à cette procédure.</p>`
      : `<table>
    <thead>
      <tr>
        <th style="width:28px">#</th>
        <th>Exigence réglementaire</th>
        <th style="width:100px">Statut</th>
        <th style="width:200px">Observations</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>`
  }
</div>

<div class="ftr">
  <span>Document confidentiel - usage interne uniquement</span>
  <strong>${esc(instNom)} · ${esc(now)}</strong>
</div>
</body>
</html>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────

export default function ProceduresInternes({ initialTab } = {}) {
  const { user } = useContext(AuthContext);
  const hasWrite = canWrite(user);
  const userRole = user?.profile?.role;
  const isAdminGlobal = userRole === "ADMIN_GLOBAL";
  const isAdminInstitution = userRole === "ADMIN_INSTITUTION";
  const isUtilisateur = userRole === "UTILISATEUR";

  // ── Tab actif ─────────────────────────────────────────────────────────────
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab !== undefined) return initialTab;
    const t = parseInt(searchParams.get("tab"), 10);
    return isNaN(t) ? 0 : t;
  });

  // ── État global ───────────────────────────────────────────────────────────
  const [selectedDomaine, setSelectedDomaine] = useState("");
  const [domaines, setDomaines] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: "", onConfirm: null });

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });

  // ── État onglet A : Procédures ────────────────────────────────────────────
  const [procedures, setProcedures] = useState([]);
  const [loadingProc, setLoadingProc] = useState(false);
  const [filterTitre, setFilterTitre] = useState("");
  const filterTitreRef = useRef(filterTitre);
  filterTitreRef.current = filterTitre;
  const [showArchived, setShowArchived] = useState(false);
  const [dialogProc, setDialogProc] = useState(false);
  const [editingProc, setEditingProc] = useState(null);
  const [formProc, setFormProc] = useState({
    titre: "",
    resume: "",
    date_adoption: "",
    domaine_id: "",
  });
  const [fileProc, setFileProc] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dialogVersions, setDialogVersions] = useState(false);
  const [versionsProc, setVersionsProc] = useState(null);
  const [dialogViewer, setDialogViewer] = useState(false);
  const [viewerUrl, setViewerUrl] = useState("");
  const [viewerTitre, setViewerTitre] = useState("");
  const viewerUrlRef = useRef("");
  viewerUrlRef.current = viewerUrl;
  useEffect(
    () => () => {
      if (viewerUrlRef.current) URL.revokeObjectURL(viewerUrlRef.current);
    },
    []
  );
  const [dialogExigences, setDialogExigences] = useState(false);
  const [allExigences, setAllExigences] = useState([]);
  const [loadingAllEx, setLoadingAllEx] = useState(false);

  // ── État onglet B : Référentiel ───────────────────────────────────────────
  const [referentiel, setReferentiel] = useState([]);
  const [loadingRef, setLoadingRef] = useState(false);
  const [referentielAll, setReferentielAll] = useState([]);
  const [totalExigencesCount, setTotalExigencesCount] = useState(null);
  const [exigencesParDomaine, setExigencesParDomaine] = useState([]);
  const [dashboardGrc, setDashboardGrc] = useState(null);
  const [procedureDetailDialog, setProcedureDetailDialog] = useState({ open: false, proc: null });
  const [filterEcart, setFilterEcart] = useState(false);
  const [filterSansProcedure, setFilterSansProcedure] = useState(false);
  const [dialogRef, setDialogRef] = useState(false);
  const [editingRef, setEditingRef] = useState(null);
  const [formRef, setFormRef] = useState({
    exigence_id: "",
    procedure_id: "",
    code: "",
    categorie: "",
    ecart_procedure: false,
    observations: "",
  });
  const [savingRef, setSavingRef] = useState(false);
  const [exigences, setExigences] = useState([]);
  const [loadingExigences, setLoadingExigences] = useState(false);
  const [dialogDomaineRef, setDialogDomaineRef] = useState("");

  // ── Chargement initial ────────────────────────────────────────────────────
  useEffect(() => {
    api
      .get("/api/domaines/")
      .then((r) => setDomaines(r.data))
      .catch(() => {});
    if (isAdminGlobal) {
      api
        .get("/api/institutions/")
        .then((r) => setInstitutions(r.data))
        .catch(() => {});
    }
  }, [isAdminGlobal]);

  // ── Chargement procédures ─────────────────────────────────────────────────
  const loadProcedures = useCallback(() => {
    setLoadingProc(true);
    const params = new URLSearchParams();
    if (selectedDomaine) params.append("domaine_id", selectedDomaine);
    if (filterTitreRef.current.trim().length >= 2)
      params.append("titre", filterTitreRef.current.trim());
    if (showArchived) params.append("include_archived", "true");
    if (isAdminGlobal && selectedInstitution) params.append("institution_id", selectedInstitution);
    api
      .get(`/api/conformite/procedures?${params}`)
      .then((r) => setProcedures(r.data))
      .catch(() => showSnack("Erreur lors du chargement des procédures.", "error"))
      .finally(() => setLoadingProc(false));
  }, [selectedDomaine, showArchived, isAdminGlobal, selectedInstitution, showSnack]);

  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  useEffect(() => {
    const t = setTimeout(loadProcedures, 300);
    return () => clearTimeout(t);
  }, [filterTitre]); // déclenche uniquement sur la frappe (debounce)

  // ── Chargement référentiel ────────────────────────────────────────────────
  const loadReferentiel = useCallback(() => {
    setLoadingRef(true);
    const params = new URLSearchParams();
    if (selectedDomaine) params.append("domaine_id", selectedDomaine);
    if (filterEcart) params.append("ecart", "true");
    if (filterSansProcedure) params.append("sans_procedure", "true");
    if (isAdminGlobal && selectedInstitution) params.append("institution_id", selectedInstitution);
    api
      .get(`/api/conformite/referentiel?${params}`)
      .then((r) => setReferentiel(r.data))
      .catch(() => showSnack("Erreur lors du chargement du référentiel.", "error"))
      .finally(() => setLoadingRef(false));
  }, [
    selectedDomaine,
    filterEcart,
    filterSansProcedure,
    isAdminGlobal,
    selectedInstitution,
    showSnack,
  ]);

  useEffect(() => {
    if (activeTab === 1 || activeTab === 2) loadReferentiel();
  }, [activeTab, loadReferentiel]);

  // ── Référentiel non filtré pour le tableau de bord ───────────────────────
  const loadReferentielAll = useCallback(() => {
    const params = new URLSearchParams();
    if (isAdminGlobal && selectedInstitution) params.append("institution_id", selectedInstitution);
    api
      .get(`/api/conformite/referentiel?${params}`)
      .then((r) => setReferentielAll(r.data))
      .catch(() => {});
  }, [isAdminGlobal, selectedInstitution]);

  // Chargement du référentiel complet au montage (nécessaire pour le modal indicateurs)
  useEffect(() => {
    loadReferentielAll();
  }, [loadReferentielAll]);

  useEffect(() => {
    if (activeTab === 2) {
      api
        .get("/api/exigences/count")
        .then((r) => setTotalExigencesCount(r.data.total))
        .catch(() => {});
      api
        .get("/api/dashboard")
        .then((r) => {
          setExigencesParDomaine(r.data.exigences_par_domaine || []);
          setDashboardGrc(r.data);
        })
        .catch(() => {});
    }
  }, [activeTab]);

  // ── Chargement exigences (formulaire B) ──────────────────────────────────
  const loadExigences = useCallback(
    (domaineOverride) => {
      setLoadingExigences(true);
      const params = new URLSearchParams();
      const dom = domaineOverride !== undefined ? domaineOverride : selectedDomaine;
      if (dom) params.append("domaine_id", dom);
      api
        .get(`/api/conformite/exigences-disponibles?${params}`)
        .then((r) => setExigences(r.data))
        .catch(() => {})
        .finally(() => setLoadingExigences(false));
    },
    [selectedDomaine]
  );

  // ── CRUD Procédures ───────────────────────────────────────────────────────
  function openCreateProc() {
    setEditingProc(null);
    setFormProc({ titre: "", resume: "", date_adoption: "", domaine_id: "" });
    setFileProc(null);
    setUploadProgress(0);
    setDialogProc(true);
  }

  function openEditProc(proc) {
    setEditingProc(proc);
    setFormProc({
      titre: proc.titre || "",
      resume: proc.resume || "",
      date_adoption: proc.date_adoption || "",
      domaine_id: proc.domaine_id || "",
    });
    setFileProc(null);
    setUploadProgress(0);
    setDialogProc(true);
  }

  async function saveProc() {
    if (!formProc.titre.trim()) {
      showSnack("Le titre est obligatoire.", "warning");
      return;
    }
    if (!editingProc && !fileProc) {
      showSnack("Un fichier PDF est obligatoire à la création.", "warning");
      return;
    }
    const fd = new FormData();
    fd.append("titre", formProc.titre.trim());
    if (formProc.resume) fd.append("resume", formProc.resume);
    if (formProc.date_adoption) fd.append("date_adoption", formProc.date_adoption);
    if (formProc.domaine_id) fd.append("domaine_id", formProc.domaine_id);
    if (fileProc) fd.append("file", fileProc);
    setSaving(true);
    setUploadProgress(0);
    try {
      if (editingProc) {
        await api.put(`/api/conformite/procedures/${editingProc.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
        });
        showSnack("Procédure mise à jour.");
      } else {
        await api.post("/api/conformite/procedures", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
        });
        showSnack("Procédure créée avec succès.");
      }
      setDialogProc(false);
      loadProcedures();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function archiveProc(proc) {
    openConfirm(`Archiver « ${proc.titre} » ?`, async () => {
      try {
        await api.delete(`/api/conformite/procedures/${proc.id}`);
        showSnack("Procédure archivée.");
        loadProcedures();
      } catch {
        showSnack("Erreur lors de l'archivage.", "error");
      }
    });
  }

  async function doWorkflowAction(proc, action, label) {
    openConfirm(`${label} « ${proc.titre} » ?`, async () => {
      try {
        await api.post(`/api/conformite/procedures/${proc.id}/${action}`);
        showSnack(`Procédure : ${label.toLowerCase()} avec succès.`);
        loadProcedures();
      } catch (e) {
        showSnack(e?.response?.data?.detail || "Erreur workflow.", "error");
      }
    });
  }

  function openVersions(proc) {
    setVersionsProc(proc);
    setDialogVersions(true);
  }

  async function toggleVersion(procId, versionId) {
    try {
      await api.patch(`/api/conformite/procedures/${procId}/versions/${versionId}/toggle`);
      const updated = await api.get(`/api/conformite/procedures?include_archived=true`);
      const proc = updated.data.find((p) => p.id === procId);
      if (proc) setVersionsProc(proc);
      loadProcedures();
    } catch {
      showSnack("Erreur lors de la mise à jour de la version.", "error");
    }
  }

  async function openViewer(proc, versionId) {
    const query = versionId ? `?version_id=${versionId}` : "";
    try {
      const response = await api.get(`/api/conformite/procedures/${proc.id}/viewer${query}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      if (viewerUrl) URL.revokeObjectURL(viewerUrl);
      setViewerUrl(blobUrl);
      setViewerTitre(proc.titre);
      setDialogViewer(true);
    } catch {
      showSnack("Impossible de charger le PDF.", "error");
    }
  }

  // ── CRUD Référentiel ──────────────────────────────────────────────────────
  function openCreateRef() {
    setDialogDomaineRef("");
    loadExigences("");
    setEditingRef(null);
    setFormRef({
      exigence_id: "",
      procedure_id: "",
      code: "",
      categorie: "",
      ecart_procedure: false,
      observations: "",
    });
    setDialogRef(true);
  }

  function openEditRef(ligne) {
    const matchedDom = domaines.find((d) => d.nom === ligne.categorie);
    const domId = matchedDom ? matchedDom.id : "";
    setDialogDomaineRef(domId);
    loadExigences(domId);
    setEditingRef(ligne);
    setFormRef({
      exigence_id: ligne.exigence_id || "",
      procedure_id: ligne.procedure_id || "",
      code: ligne.code || "",
      categorie: ligne.categorie || "",
      ecart_procedure: ligne.ecart_procedure || false,
      observations: ligne.observations || "",
    });
    setDialogRef(true);
  }

  async function saveRef() {
    if (!formRef.exigence_id) {
      showSnack("L'exigence est obligatoire.", "warning");
      return;
    }
    const payload = {
      exigence_id: Number(formRef.exigence_id),
      procedure_id: formRef.procedure_id ? Number(formRef.procedure_id) : null,
      code: formRef.code || null,
      categorie: domaines.find((d) => d.id === Number(dialogDomaineRef))?.nom || null,
      ecart_procedure: formRef.ecart_procedure,
      observations: formRef.observations || null,
    };
    setSavingRef(true);
    try {
      if (editingRef) {
        await api.put(`/api/conformite/referentiel/${editingRef.id}`, payload);
        showSnack("Ligne mise à jour.");
      } else {
        const res = await api.post("/api/conformite/referentiel", payload);
        showSnack(`Exigence ${res.data.code} ajoutée au référentiel.`);
      }
      setDialogRef(false);
      loadReferentiel();
    } catch (err) {
      showSnack(err.response?.data?.detail || "Erreur lors de l'enregistrement.", "error");
    } finally {
      setSavingRef(false);
    }
  }

  async function deleteRef(ligne) {
    openConfirm("Supprimer cette ligne du référentiel ?", async () => {
      try {
        await api.delete(`/api/conformite/referentiel/${ligne.id}`);
        showSnack("Ligne supprimée.");
        loadReferentiel();
      } catch {
        showSnack("Erreur lors de la suppression.", "error");
      }
    });
  }

  // ── Colonnes Procédures ───────────────────────────────────────────────────
  const proceduresColumns = useMemo(
    () => [
      {
        Header: "Procédure",
        accessor: "titre",
        width: "40%",
        wrap: true,
        Cell: ({ row }) => {
          const proc = row.original;
          return (
            <MDBox>
              <MDTypography variant="body2" fontWeight="medium">
                {proc.titre}
              </MDTypography>
              {proc.resume && (
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", lineHeight: 1.5, mt: 0.25 }}
                >
                  {proc.resume}
                </MDTypography>
              )}
              {proc.date_adoption && (
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Adoptée le {new Date(proc.date_adoption).toLocaleDateString("fr-FR")}
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Domaine",
        accessor: "domaine",
        width: "20%",
        wrap: true,
        Cell: ({ row }) => (
          <MDTypography variant="caption">{row.original.domaine?.nom || "-"}</MDTypography>
        ),
      },
      {
        Header: "Statut",
        accessor: "statut",
        width: "18%",
        Cell: ({ row }) => {
          const proc = row.original;
          const wfColors = {
            BROUILLON: { bg: "#f5f5f5", color: "#757575", icon: "edit_note" },
            EN_REVISION: { bg: "#e3f2fd", color: "#1565c0", icon: "rate_review" },
            APPROUVE: { bg: "#e8f5e9", color: "#2e7d32", icon: "check_circle" },
            PUBLIE: { bg: "#e0f7fa", color: "#006064", icon: "publish" },
          };
          const wf = wfColors[proc.workflow_statut] || wfColors.PUBLIE;
          return (
            <MDBox display="flex" flexDirection="column" gap={0.5}>
              <StatutChip statut={proc.statut} />
              <Chip
                icon={
                  <Icon sx={{ fontSize: "0.8rem !important", color: `${wf.color} !important` }}>
                    {wf.icon}
                  </Icon>
                }
                label={WF_LABEL[proc.workflow_statut] || WF_LABEL.PUBLIE}
                size="small"
                sx={{ bgcolor: wf.bg, color: wf.color, fontWeight: 600, fontSize: "0.65rem" }}
              />
            </MDBox>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "22%",
        Cell: ({ row }) => {
          const proc = row.original;
          const activeVersion = (proc.versions || [])
            .filter((v) => v.actif)
            .sort((a, b) => new Date(b.date_version) - new Date(a.date_version))[0];
          const wf = proc.workflow_statut || "PUBLIE";
          return (
            <MDBox display="flex" flexWrap="wrap" gap={0.5}>
              {activeVersion && (
                <>
                  <Tooltip title="Voir le PDF">
                    <IconButton
                      size="small"
                      onClick={() => openViewer(proc)}
                      sx={{ color: "#186A81" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>visibility</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Historique des versions">
                    <IconButton
                      size="small"
                      onClick={() => openVersions(proc)}
                      sx={{ color: "#555" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>history</Icon>
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Indicateurs">
                <IconButton
                  size="small"
                  onClick={() => setProcedureDetailDialog({ open: true, proc })}
                  sx={{ color: "#6a1b9a" }}
                >
                  <Icon sx={{ fontSize: "1rem" }}>bar_chart</Icon>
                </IconButton>
              </Tooltip>
              {hasWrite && (
                <Tooltip title="Modifier">
                  <IconButton
                    size="small"
                    onClick={() => openEditProc(proc)}
                    sx={{ color: "#0A3475" }}
                  >
                    <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {/* ── Boutons workflow ── */}
              {hasWrite && wf === "BROUILLON" && (
                <Tooltip title="Soumettre pour révision">
                  <IconButton
                    size="small"
                    onClick={() => doWorkflowAction(proc, "soumettre", "Soumettre")}
                    sx={{ color: "#1565c0" }}
                  >
                    <Icon sx={{ fontSize: "1rem" }}>send</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {(isAdminInstitution || isAdminGlobal) && wf === "EN_REVISION" && (
                <>
                  <Tooltip title="Approuver">
                    <IconButton
                      size="small"
                      onClick={() => doWorkflowAction(proc, "approuver", "Approuver")}
                      sx={{ color: "#2e7d32" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>check_circle</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Rejeter (retour brouillon)">
                    <IconButton
                      size="small"
                      onClick={() => doWorkflowAction(proc, "rejeter", "Rejeter")}
                      sx={{ color: "#c62828" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>cancel</Icon>
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {(isAdminInstitution || isAdminGlobal) && wf === "APPROUVE" && (
                <Tooltip title="Publier">
                  <IconButton
                    size="small"
                    onClick={() => doWorkflowAction(proc, "publier", "Publier")}
                    sx={{ color: "#006064" }}
                  >
                    <Icon sx={{ fontSize: "1rem" }}>publish</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {hasWrite && proc.statut === "ACTIF" && (
                <Tooltip title="Archiver">
                  <IconButton
                    size="small"
                    onClick={() => archiveProc(proc)}
                    sx={{ color: "#d32f2f" }}
                  >
                    <Icon sx={{ fontSize: "1rem" }}>archive</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
    ],
    [hasWrite, isAdminInstitution, isAdminGlobal]
  );

  // ── Colonnes Référentiel ──────────────────────────────────────────────────
  const referentielColumns = useMemo(
    () => [
      {
        Header: "Exigence",
        accessor: "exigence",
        width: "42%",
        wrap: true,
        Cell: ({ row }) => {
          const ligne = row.original;
          return (
            <MDBox>
              {ligne.code && (
                <Chip
                  label={ligne.code}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.65rem", fontWeight: 600, height: 18, mb: 0.5 }}
                />
              )}
              <MDTypography variant="caption" sx={{ lineHeight: 1.5, wordBreak: "break-word" }}>
                {ligne.exigence?.exigence || "-"}
              </MDTypography>
              <MDBox display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                {(ligne.texte_titre || ligne.exigence?.reference) && (
                  <MDBox display="flex" alignItems="center" gap={0.4}>
                    <Icon sx={{ fontSize: "11px", color: "#9e9e9e" }}>tag</Icon>
                    <MDTypography variant="caption" color="text" sx={{ fontSize: "0.68rem" }}>
                      {[ligne.texte_titre, ligne.exigence?.reference].filter(Boolean).join(" - ")}
                    </MDTypography>
                  </MDBox>
                )}
                {ligne.categorie && (
                  <MDBox display="flex" alignItems="center" gap={0.4}>
                    <Icon sx={{ fontSize: "11px", color: "#186A81" }}>domain</Icon>
                    <MDTypography variant="caption" sx={{ fontSize: "0.68rem", color: "#186A81" }}>
                      {ligne.categorie}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          );
        },
      },
      {
        Header: "Procédure / Statut",
        accessor: "procedure",
        width: "28%",
        wrap: true,
        Cell: ({ row }) => {
          const ligne = row.original;
          return (
            <MDBox>
              {ligne.procedure ? (
                <MDTypography
                  variant="caption"
                  fontWeight="medium"
                  sx={{
                    color: "#186A81",
                    cursor: "pointer",
                    lineHeight: 1.5,
                    wordBreak: "break-word",
                    "&:hover": { color: "#0A3475", textDecoration: "underline" },
                  }}
                  onClick={() => openViewer(ligne.procedure)}
                >
                  {ligne.procedure.titre}
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
                  Aucune procédure liée
                </MDTypography>
              )}
              <MDBox mt={0.5}>
                <EcartBadge ligne={ligne} />
              </MDBox>
            </MDBox>
          );
        },
      },
      {
        Header: "Domaine / Notes",
        accessor: "domaine_nom",
        width: "20%",
        wrap: true,
        Cell: ({ row }) => {
          const ligne = row.original;
          return (
            <MDBox>
              {ligne.domaine_nom ? (
                <Chip
                  label={ligne.domaine_nom}
                  size="small"
                  sx={{
                    bgcolor: "#e8edf5",
                    color: "#0A3475",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    height: 20,
                    maxWidth: "100%",
                    mb: 0.5,
                  }}
                />
              ) : null}
              {ligne.observations && (
                <Tooltip title={ligne.observations} placement="top">
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      cursor: "help",
                      fontSize: "0.68rem",
                      fontStyle: "italic",
                    }}
                  >
                    {ligne.observations}
                  </MDTypography>
                </Tooltip>
              )}
            </MDBox>
          );
        },
      },
      ...(hasWrite
        ? [
            {
              Header: "Actions",
              accessor: "ref_actions",
              width: "10%",
              Cell: ({ row }) => (
                <MDBox display="flex" gap={0.5}>
                  <Tooltip title="Modifier">
                    <IconButton
                      size="small"
                      onClick={() => openEditRef(row.original)}
                      sx={{ color: "#0A3475" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton
                      size="small"
                      onClick={() => deleteRef(row.original)}
                      sx={{ color: "#d32f2f" }}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>delete</Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              ),
            },
          ]
        : []),
    ],
    [hasWrite]
  );

  // ── Rendu Tab A ───────────────────────────────────────────────────────────
  function renderTabA() {
    return (
      <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
        {/* Filtres */}
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
          <MDInput
            placeholder="Rechercher par titre..."
            value={filterTitre}
            onChange={(e) => setFilterTitre(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <Icon sx={{ mr: 1, color: "text.secondary", fontSize: "1.1rem" }}>search</Icon>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <Select
              displayEmpty
              value={selectedDomaine}
              onChange={(e) => setSelectedDomaine(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  domaines.find((d) => d.id === v)?.nom
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
                )
              }
            >
              <MenuItem value="">Tous les domaines</MenuItem>
              {domaines.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setShowArchived((v) => !v)}
          >
            <MDBox
              sx={{
                position: "relative",
                width: 36,
                height: 20,
                borderRadius: "10px",
                background: showArchived ? "linear-gradient(135deg, #0A3475, #186A81)" : "#d0d7e2",
                transition: "background 0.25s",
                flexShrink: 0,
              }}
            >
              <MDBox
                sx={{
                  position: "absolute",
                  top: 3,
                  left: showArchived ? 19 : 3,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                  transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: showArchived ? "#0A3475" : "#888",
                fontWeight: showArchived ? 600 : 400,
                transition: "color 0.2s",
              }}
            >
              Afficher les archivées
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1} sx={{ ml: "auto" }}>
            {!isUtilisateur && (
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<Icon>picture_as_pdf</Icon>}
                onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (isAdminGlobal && selectedInstitution) {
                      params.append("institution_id", selectedInstitution);
                    }
                    const [procRes, refRes] = await Promise.all([
                      api.get(`/api/conformite/procedures?include_archived=true&${params}`),
                      api.get(`/api/conformite/referentiel?${params}`),
                    ]);
                    let instNom = "ConformIMF";
                    if (isAdminGlobal) {
                      if (selectedInstitution) {
                        const inst = institutions.find((i) => i.id === Number(selectedInstitution));
                        instNom = inst?.nom || `Institution #${selectedInstitution}`;
                      } else {
                        instNom = "Toutes les institutions";
                      }
                    } else {
                      instNom = user?.profile?.roles_details?.[0]?.entite?.nom || "Institution";
                    }
                    const html = buildRapportProceduressHtml(procRes.data, refRes.data, instNom);
                    const win = window.open("", "_blank", "width=1000,height=800");
                    if (!win) {
                      showSnack(
                        "Popups bloqués par le navigateur. Autorisez-les pour ce site.",
                        "warning"
                      );
                      return;
                    }
                    win.document.write(html);
                    win.document.close();
                  } catch {
                    showSnack("Erreur lors de la génération du rapport.", "error");
                  }
                }}
                sx={{ height: "45px" }}
              >
                Rapport PDF
              </MDButton>
            )}
            {hasWrite && (
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                startIcon={<Icon>add</Icon>}
                onClick={openCreateProc}
                sx={{ height: "45px" }}
              >
                Nouvelle procédure
              </MDButton>
            )}
          </MDBox>
        </MDBox>
        {/* Table */}
        {loadingProc ? (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </MDBox>
        ) : !procedures.length ? (
          <MDBox textAlign="center" py={4}>
            <Icon sx={{ fontSize: 48, color: "text.disabled" }}>description</Icon>
            <MDTypography variant="body2" color="text">
              Aucune procédure trouvée.
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox sx={{ overflowX: "auto" }}>
            <DataTable
              table={{ columns: proceduresColumns, rows: procedures }}
              isSorted={false}
              entriesPerPage
              showTotalEntries
            />
          </MDBox>
        )}
      </MDBox>
    );
  }

  // ── Rendu Tab B ───────────────────────────────────────────────────────────
  function renderTabB() {
    return (
      <MDBox sx={{ animation: `${fadeInUp} 0.3s ease both` }}>
        {/* Filtres */}
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              displayEmpty
              value={selectedDomaine}
              onChange={(e) => setSelectedDomaine(e.target.value)}
              sx={{ height: "45px" }}
              renderValue={(v) =>
                v ? (
                  domaines.find((d) => d.id === v)?.nom
                ) : (
                  <span style={{ color: "#aaa", fontSize: "0.875rem" }}>Tous les domaines</span>
                )
              }
            >
              <MenuItem value="">Tous les domaines</MenuItem>
              {domaines.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setFilterEcart((v) => !v)}
          >
            <MDBox
              sx={{
                position: "relative",
                width: 36,
                height: 20,
                borderRadius: "10px",
                background: filterEcart ? "linear-gradient(135deg, #e65100, #fb8c00)" : "#d0d7e2",
                transition: "background 0.25s",
                flexShrink: 0,
              }}
            >
              <MDBox
                sx={{
                  position: "absolute",
                  top: 3,
                  left: filterEcart ? 19 : 3,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                  transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: filterEcart ? "#e65100" : "#888",
                fontWeight: filterEcart ? 600 : 400,
                transition: "color 0.2s",
              }}
            >
              Écarts uniquement
            </MDTypography>
          </MDBox>
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setFilterSansProcedure((v) => !v)}
          >
            <MDBox
              sx={{
                position: "relative",
                width: 36,
                height: 20,
                borderRadius: "10px",
                background: filterSansProcedure
                  ? "linear-gradient(135deg, #b71c1c, #e53935)"
                  : "#d0d7e2",
                transition: "background 0.25s",
                flexShrink: 0,
              }}
            >
              <MDBox
                sx={{
                  position: "absolute",
                  top: 3,
                  left: filterSansProcedure ? 19 : 3,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                  transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </MDBox>
            <MDTypography
              variant="caption"
              sx={{
                color: filterSansProcedure ? "#b71c1c" : "#888",
                fontWeight: filterSansProcedure ? 600 : 400,
                transition: "color 0.2s",
              }}
            >
              Sans procédure
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<Icon>download</Icon>}
            onClick={() =>
              downloadCSV(
                "referentiel_exigences",
                referentiel.map((l) => ({
                  code: l.code || "-",
                  texte_titre: l.texte_titre || "",
                  exigence_ref: l.exigence?.reference || "",
                  exigence_txt: l.exigence?.exigence || "",
                  procedure_titre: l.procedure?.titre || "",
                  conformite: !l.procedure_id
                    ? "Non couverte"
                    : l.ecart_procedure
                    ? "Écart"
                    : "Conforme",
                  observations: l.observations || "",
                })),
                [
                  { key: "code", label: "Code" },
                  { key: "texte_titre", label: "Texte réglementaire" },
                  { key: "exigence_ref", label: "Référence" },
                  { key: "exigence_txt", label: "Exigence" },
                  { key: "procedure_titre", label: "Procédure liée" },
                  { key: "conformite", label: "Conformité" },
                  { key: "observations", label: "Observations" },
                ]
              )
            }
            sx={{ height: "45px" }}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Icon>table_view</Icon>}
            onClick={async () => {
              setDialogExigences(true);
              if (allExigences.length === 0) {
                setLoadingAllEx(true);
                try {
                  const res = await api.get(
                    "/api/conformite/exigences-disponibles?actifs_seulement=true"
                  );
                  setAllExigences(res.data);
                } catch {
                  showSnack("Erreur lors du chargement des exigences.", "error");
                } finally {
                  setLoadingAllEx(false);
                }
              }
            }}
            sx={{ ml: "auto", height: "45px" }}
          >
            Toutes les exigences
          </MDButton>
          {hasWrite && (
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              startIcon={<Icon>add</Icon>}
              onClick={openCreateRef}
              sx={{ ml: 1, height: "45px" }}
            >
              Ajouter une ligne
            </MDButton>
          )}
        </MDBox>
        {/* Table */}
        {loadingRef ? (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </MDBox>
        ) : !referentiel.length ? (
          <MDBox textAlign="center" py={6}>
            <Icon sx={{ fontSize: 48, color: "text.disabled" }}>fact_check</Icon>
            <MDTypography variant="body2" color="text" mt={1}>
              Aucune ligne dans le référentiel.{" "}
              {hasWrite && "Cliquez sur « Ajouter une ligne » pour commencer."}
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox>
            <DataTable
              table={{ columns: referentielColumns, rows: referentiel }}
              isSorted={false}
              entriesPerPage
              showTotalEntries
            />
            <MDBox display="flex" gap={2} mt={1} flexWrap="wrap">
              <MDTypography variant="caption" color="text">
                {referentiel.length} ligne{referentiel.length > 1 ? "s" : ""} au total
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#c62828" }}>
                · {referentiel.filter((l) => !l.procedure_id).length} sans procédure
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#e65100" }}>
                · {referentiel.filter((l) => l.ecart_procedure).length} écart
                {referentiel.filter((l) => l.ecart_procedure).length > 1 ? "s" : ""}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#2e7d32" }}>
                · {referentiel.filter((l) => l.procedure_id && !l.ecart_procedure).length} conforme
                {referentiel.filter((l) => l.procedure_id && !l.ecart_procedure).length > 1
                  ? "s"
                  : ""}
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    );
  }

  // ── Dialogues ─────────────────────────────────────────────────────────────
  function renderDialogProc() {
    return (
      <Dialog open={dialogProc} onClose={() => setDialogProc(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingProc ? "Modifier la procédure" : "Nouvelle procédure"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Titre *"
              fullWidth
              size="small"
              value={formProc.titre}
              onChange={(e) => setFormProc((p) => ({ ...p, titre: e.target.value }))}
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="Résumé"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={formProc.resume}
              onChange={(e) => setFormProc((p) => ({ ...p, resume: e.target.value }))}
              inputProps={{ maxLength: 1000 }}
              helperText={`${formProc.resume.length}/1000`}
            />
            <TextField
              label="Date d'adoption"
              type="date"
              fullWidth
              size="small"
              value={formProc.date_adoption}
              onChange={(e) => setFormProc((p) => ({ ...p, date_adoption: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Domaine</InputLabel>
              <Select
                value={formProc.domaine_id}
                label="Domaine"
                onChange={(e) => setFormProc((p) => ({ ...p, domaine_id: e.target.value }))}
              >
                <MenuItem value="">Aucun</MenuItem>
                {domaines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MDBox>
              <MDTypography variant="caption" color="text" mb={0.5} display="block">
                {editingProc
                  ? "Nouveau PDF (optionnel - crée une nouvelle version)"
                  : "Fichier PDF *"}
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                component="label"
                startIcon={<Icon>upload_file</Icon>}
              >
                {fileProc ? fileProc.name : "Choisir un fichier PDF"}
                <input
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => setFileProc(e.target.files[0] || null)}
                />
              </MDButton>
            </MDBox>
            {saving && uploadProgress > 0 && (
              <MDBox>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <MDTypography variant="caption">{uploadProgress}%</MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogProc(false)}
            disabled={saving}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveProc}
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  function renderDialogRef() {
    const selectedExigence = exigences.find((e) => e.id === Number(formRef.exigence_id)) || null;
    const activeProcedures = procedures.filter((p) => p.statut === "ACTIF");
    return (
      <Dialog open={dialogRef} onClose={() => setDialogRef(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">
            {editingRef ? "Modifier la ligne de référentiel" : "Ajouter une ligne au référentiel"}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Code"
              fullWidth
              size="small"
              value={editingRef ? formRef.code : "Auto"}
              InputProps={{ readOnly: !editingRef }}
              onChange={(e) => editingRef && setFormRef((f) => ({ ...f, code: e.target.value }))}
              inputProps={{ maxLength: 20 }}
              helperText={editingRef ? "" : "Généré par le serveur à l'enregistrement"}
              sx={{ "& input": { color: editingRef ? "inherit" : "#9e9e9e" } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Domaine</InputLabel>
              <Select
                value={dialogDomaineRef}
                label="Domaine"
                onChange={(e) => {
                  const val = e.target.value;
                  setDialogDomaineRef(val);
                  setFormRef((f) => ({ ...f, exigence_id: "" }));
                  loadExigences(val);
                }}
              >
                <MenuItem value="">Tous les domaines</MenuItem>
                {domaines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              options={exigences}
              getOptionLabel={(e) =>
                `${e.texte_titre ? `${e.texte_titre} - ` : ""}${
                  e.reference ? `[${e.reference}] ` : ""
                }${e.exigence.slice(0, 80)}${e.exigence.length > 80 ? "…" : ""}`
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <MDBox display="flex" flexDirection="column" py={0.5}>
                    {option.texte_titre && (
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                        sx={{ opacity: 0.65, lineHeight: 1.3, mb: 0.25 }}
                      >
                        {option.texte_titre}
                      </MDTypography>
                    )}
                    <MDTypography variant="button" fontWeight="regular" sx={{ lineHeight: 1.4 }}>
                      {option.reference ? `[${option.reference}] ` : ""}
                      {option.exigence.slice(0, 120)}
                      {option.exigence.length > 120 ? "…" : ""}
                    </MDTypography>
                  </MDBox>
                </li>
              )}
              value={selectedExigence}
              onChange={(_, val) =>
                setFormRef((f) => ({
                  ...f,
                  exigence_id: val ? val.id : "",
                }))
              }
              loading={loadingExigences}
              disabled={!!editingRef}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Exigence *"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingExigences && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="Aucune exigence trouvée"
            />
            {selectedExigence && (
              <MDBox
                sx={{
                  borderLeft: "3px solid",
                  borderColor: "info.main",
                  bgcolor: "rgba(26, 115, 232, 0.06)",
                  borderRadius: "0 6px 6px 0",
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {selectedExigence.texte_titre && (
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: "14px !important", color: "info.main", mt: "1px" }}>
                      gavel
                    </Icon>
                    <MDTypography variant="caption" color="info" fontWeight="medium">
                      {selectedExigence.texte_titre}
                    </MDTypography>
                  </MDBox>
                )}
                {selectedExigence.domaine_nom && (
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: "14px !important", color: "text.secondary", mt: "1px" }}>
                      category
                    </Icon>
                    <MDTypography variant="caption" color="text">
                      {selectedExigence.domaine_nom}
                    </MDTypography>
                  </MDBox>
                )}
                {selectedExigence.reference && (
                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    <Icon sx={{ fontSize: "14px !important", color: "text.secondary", mt: "1px" }}>
                      tag
                    </Icon>
                    <MDTypography variant="caption" color="text" fontWeight="medium">
                      {selectedExigence.reference}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox display="flex" gap={0.75} pt={0.25}>
                  <Icon
                    sx={{
                      fontSize: "14px !important",
                      color: "text.secondary",
                      mt: "1px",
                      flexShrink: 0,
                    }}
                  >
                    description
                  </Icon>
                  <MDTypography variant="caption" color="text" sx={{ lineHeight: 1.5 }}>
                    {selectedExigence.exigence}
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Procédure interne</InputLabel>
              <Select
                value={formRef.procedure_id}
                label="Procédure interne"
                onChange={(e) => {
                  const val = e.target.value;
                  setFormRef((f) => ({
                    ...f,
                    procedure_id: val,
                    ecart_procedure: val ? f.ecart_procedure : false,
                  }));
                }}
              >
                <MenuItem value="">Aucune</MenuItem>
                {activeProcedures.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.titre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formRef.procedure_id && (
              <MDBox
                onClick={() => setFormRef((f) => ({ ...f, ecart_procedure: !f.ecart_procedure }))}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: formRef.ecart_procedure ? "#e65100" : "#2e7d32",
                  bgcolor: formRef.ecart_procedure ? "#fff8e1" : "#e8f5e9",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  userSelect: "none",
                }}
              >
                <Checkbox
                  checked={formRef.ecart_procedure}
                  onChange={(e) => {
                    e.stopPropagation();
                    setFormRef((f) => ({ ...f, ecart_procedure: e.target.checked }));
                  }}
                  color={formRef.ecart_procedure ? "warning" : "success"}
                  size="small"
                  sx={{ p: 0 }}
                />
                <MDBox>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      color: formRef.ecart_procedure ? "#e65100" : "#2e7d32",
                      display: "block",
                    }}
                  >
                    Signaler un écart de procédure
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "#888", fontSize: "0.68rem" }}>
                    La procédure ne couvre pas complètement cette exigence
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
            <TextField
              label="Observations"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={formRef.observations}
              onChange={(e) => setFormRef((f) => ({ ...f, observations: e.target.value }))}
              inputProps={{ maxLength: 2000 }}
              helperText={`${formRef.observations.length}/2000`}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogRef(false)}
            disabled={savingRef}
          >
            Annuler
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={saveRef}
            disabled={savingRef}
          >
            {savingRef ? <CircularProgress size={16} color="inherit" /> : "Enregistrer"}
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  function renderDialogExigences() {
    const grouped = {};
    allExigences.forEach((e) => {
      const dom = e.domaine_nom || "Sans domaine";
      if (!grouped[dom]) grouped[dom] = [];
      grouped[dom].push(e);
    });
    const domainList = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    const total = allExigences.length;
    const totLabel = `${total} exigence${total > 1 ? "s" : ""}`;

    function handlePrint() {
      const html = buildExigencesHtml(allExigences);
      const win = window.open("", "_blank", "width=1100,height=850");
      if (!win) {
        showSnack("Popups bloqués par le navigateur. Autorisez-les pour ce site.", "warning");
        return;
      }
      win.document.write(html);
      win.document.close();
    }

    return (
      <Dialog
        open={dialogExigences}
        onClose={() => setDialogExigences(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ color: "#0A3475", fontSize: "1.3rem" }}>menu_book</Icon>
              <MDTypography variant="h6" fontWeight="bold">
                Exigences réglementaires par domaine
              </MDTypography>
              {!loadingAllEx && (
                <Chip
                  label={totLabel}
                  size="small"
                  sx={{ bgcolor: "#eef2f8", color: "#0A3475", fontWeight: 600 }}
                />
              )}
            </MDBox>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<Icon>print</Icon>}
                onClick={handlePrint}
                disabled={loadingAllEx || total === 0}
              >
                Imprimer / PDF
              </MDButton>
              <IconButton size="small" onClick={() => setDialogExigences(false)}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loadingAllEx ? (
            <MDBox display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </MDBox>
          ) : total === 0 ? (
            <MDBox textAlign="center" py={6}>
              <Icon sx={{ fontSize: 48, color: "text.disabled" }}>menu_book</Icon>
              <MDTypography variant="body2" color="text" mt={1}>
                Aucune exigence dans les textes actifs.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox>
              {domainList.map((dom) => {
                const items = grouped[dom];
                const pl = items.length > 1 ? "s" : "";
                const countLbl = `${items.length} exigence${pl}`;
                return (
                  <MDBox key={dom} sx={{ borderBottom: "2px solid #f0f2f5" }}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      sx={{
                        px: 3,
                        py: 1.5,
                        bgcolor: "#eef2f8",
                        borderLeft: "4px solid #0A3475",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <Icon sx={{ color: "#0A3475", fontSize: "1.1rem" }}>folder_open</Icon>
                      <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#0A3475" }}>
                        {dom}
                      </MDTypography>
                      <Chip
                        label={countLbl}
                        size="small"
                        sx={{
                          bgcolor: "#fff",
                          color: "#7a8eab",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          border: "1px solid #c8d8ee",
                        }}
                      />
                    </MDBox>
                    <MDBox sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 480 }}>
                        <colgroup>
                          <col style={{ width: 100 }} />
                          <col style={{ width: "auto" }} />
                          <col style={{ width: 180 }} />
                        </colgroup>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "#f5f7fb" }}>
                            {["Référence", "Exigence", "Texte réglementaire"].map((h) => (
                              <TableCell
                                key={h}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.73rem",
                                  color: "#0A3475",
                                  py: 0.8,
                                  borderBottom: "2px solid #e0e8f0",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((e) => (
                            <TableRow key={e.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                              <TableCell
                                sx={{
                                  py: 0.8,
                                  fontWeight: 700,
                                  color: "#0A3475",
                                  fontSize: "0.72rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {e.reference || "-"}
                              </TableCell>
                              <TableCell sx={{ py: 0.8 }}>
                                <MDTypography
                                  variant="caption"
                                  sx={{ lineHeight: 1.5, display: "block" }}
                                >
                                  {e.exigence || "-"}
                                </MDTypography>
                              </TableCell>
                              <TableCell sx={{ py: 0.8 }}>
                                <MDTypography
                                  variant="caption"
                                  sx={{
                                    color: "#666",
                                    fontSize: "0.68rem",
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={e.texte_titre || "-"}
                                >
                                  {e.texte_titre || "-"}
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  function renderDialogVersions() {
    if (!versionsProc) return null;
    const versions = [...(versionsProc.versions || [])].sort(
      (a, b) => new Date(b.date_version) - new Date(a.date_version)
    );
    return (
      <Dialog
        open={dialogVersions}
        onClose={() => setDialogVersions(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold">
            Versions - {versionsProc.titre}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          {versions.length === 0 ? (
            <MDTypography variant="body2" color="text">
              Aucune version disponible.
            </MDTypography>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 560, tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 80 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 90 }} />
                </colgroup>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                    {[
                      { label: "Version", align: "center" },
                      { label: "Date" },
                      { label: "Fichier" },
                      { label: "Statut", align: "center" },
                      { label: "Actions", align: "center" },
                    ].map((h) => (
                      <TableCell
                        key={h.label}
                        align={h.align || "left"}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          color: "#4B5E80",
                          py: 1,
                          borderBottom: "2px solid #e0e8f0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versions.map((v) => {
                    const doc = v.documents?.[v.documents.length - 1];
                    return (
                      <TableRow
                        key={v.id}
                        sx={{ "&:last-child td": { border: 0 }, "&:hover": { bgcolor: "#f8fafc" } }}
                      >
                        <TableCell align="center" sx={{ py: 1 }}>
                          <Chip
                            label={`v${v.version}`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1, whiteSpace: "nowrap" }}>
                          <MDTypography variant="caption" sx={{ fontSize: "0.75rem" }}>
                            {new Date(v.date_version).toLocaleDateString("fr-FR")}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <MDTypography
                            variant="caption"
                            sx={{
                              fontSize: "0.75rem",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={doc?.nom_fichier || "-"}
                          >
                            {doc?.nom_fichier || "-"}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <Chip
                            label={v.actif ? "Actif" : "Inactif"}
                            size="small"
                            sx={{
                              bgcolor: v.actif ? "#e8f5e9" : "#f5f5f5",
                              color: v.actif ? "#2e7d32" : "#757575",
                              fontWeight: 600,
                              fontSize: "0.68rem",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <MDBox display="flex" gap={0.5} justifyContent="center">
                            {doc && (
                              <Tooltip title="Voir le PDF">
                                <IconButton
                                  size="small"
                                  onClick={() => openViewer(versionsProc, v.id)}
                                  sx={{ color: "#186A81" }}
                                >
                                  <Icon sx={{ fontSize: "1rem" }}>visibility</Icon>
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasWrite && (
                              <Tooltip title={v.actif ? "Désactiver" : "Activer"}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleVersion(versionsProc.id, v.id)}
                                  sx={{ color: v.actif ? "#d32f2f" : "#2e7d32" }}
                                >
                                  <Icon sx={{ fontSize: "1rem" }}>
                                    {v.actif ? "toggle_off" : "toggle_on"}
                                  </Icon>
                                </IconButton>
                              </Tooltip>
                            )}
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => setDialogVersions(false)}
          >
            Fermer
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  function renderDialogViewer() {
    function closeViewer() {
      setDialogViewer(false);
      if (viewerUrl) {
        URL.revokeObjectURL(viewerUrl);
        setViewerUrl("");
      }
    }
    return (
      <Dialog open={dialogViewer} onClose={closeViewer} maxWidth="lg" fullWidth>
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h6" fontWeight="bold">
              {viewerTitre}
            </MDTypography>
            <IconButton size="small" onClick={closeViewer}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "75vh" }}>
          <iframe
            src={viewerUrl}
            title={viewerTitre}
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // ── Tableau de bord ───────────────────────────────────────────────────────
  function renderTabC() {
    const actifs = procedures.filter((p) => p.statut === "ACTIF").length;
    const archivees = procedures.filter((p) => p.statut === "ARCHIVE").length;
    const enValidation = procedures.filter((p) => p.statut === "EN_VALIDATION").length;
    const total = procedures.length;

    // Valeurs calculées côté backend (institution-scoped, dénominateur correct)
    // Une exigence n'est "conforme" que si elle est rattachée à une procédure ET sans écart -
    // couverture et conformité sont un seul et même concept dans toute cette page.
    const totalRefLines = dashboardGrc?.exigences_total ?? referentielAll.length;
    const rattachees =
      dashboardGrc?.exigences_couvertes ?? referentielAll.filter((l) => l.procedure_id).length;
    const ecarts =
      dashboardGrc?.ecarts_signales ?? referentielAll.filter((l) => l.ecart_procedure).length;
    const conformes = rattachees - ecarts;
    const nonConformes = totalRefLines - conformes;
    const taux = totalRefLines > 0 ? Math.round((conformes / totalRefLines) * 100) : 0;
    const tauxNonConf = 100 - taux;

    const domainCounts = {};
    procedures.forEach((p) => {
      const dom = p.domaine?.nom || "Sans domaine";
      domainCounts[dom] = (domainCounts[dom] || 0) + 1;
    });
    const sortedDomains = Object.entries(domainCounts).sort(([, a], [, b]) => b - a);

    const panel = {
      flex: "1 1 calc(50% - 8px)",
      minWidth: 260,
      p: 2.5,
      borderRadius: "10px",
      background: "#fff",
      border: "1px solid #e8ecf4",
      boxShadow: "0 2px 8px rgba(10,52,117,0.06)",
    };

    return (
      <MDBox>
        {/* ── KPIs ── */}
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
          {/* Procédures actives */}
          <MDBox sx={{ ...panel, borderLeft: "4px solid #0A3475" }}>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <Icon sx={{ color: "#0A3475", fontSize: "1.1rem" }}>description</Icon>
              <MDTypography variant="caption" sx={{ color: "#6b7a99", fontWeight: 600 }}>
                Procédures actives
              </MDTypography>
            </MDBox>
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#0A3475" }}>
              {actifs}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
              {enValidation > 0 ? `${enValidation} en validation - ` : ""}
              {archivees} archivée{archivees !== 1 ? "s" : ""} - {total} au total
            </MDTypography>
          </MDBox>

          {/* Référentiel */}
          <MDBox sx={{ ...panel, borderLeft: "4px solid #0A3475" }}>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <Icon sx={{ color: "#0A3475", fontSize: "1.1rem" }}>menu_book</Icon>
              <MDTypography variant="caption" sx={{ color: "#6b7a99", fontWeight: 600 }}>
                Total des exigences réglementaires liées
              </MDTypography>
            </MDBox>
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#0A3475" }}>
              {totalRefLines}
            </MDTypography>
          </MDBox>

          {/* Exigences couvertes */}
          <MDBox sx={{ ...panel, borderLeft: "4px solid #2e7d32" }}>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <Icon sx={{ color: "#2e7d32", fontSize: "1.1rem" }}>check_circle</Icon>
              <MDTypography variant="caption" sx={{ color: "#6b7a99", fontWeight: 600 }}>
                Exigences couvertes
              </MDTypography>
            </MDBox>
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#2e7d32" }}>
              {conformes}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
              sur {totalRefLines} dans le référentiel - rattachées et sans écart
            </MDTypography>
          </MDBox>

          {/* Exigences non couvertes */}
          <MDBox
            sx={{
              ...panel,
              borderLeft: `4px solid ${nonConformes > 0 ? "#c62828" : "#9e9e9e"}`,
              cursor: "pointer",
            }}
            onClick={() => {
              setFilterEcart(true);
              setActiveTab(1);
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <Icon sx={{ color: nonConformes > 0 ? "#c62828" : "#9e9e9e", fontSize: "1.1rem" }}>
                warning
              </Icon>
              <MDTypography variant="caption" sx={{ color: "#6b7a99", fontWeight: 600 }}>
                Exigences non couvertes
              </MDTypography>
            </MDBox>
            <MDTypography
              variant="h4"
              fontWeight="bold"
              sx={{ color: nonConformes > 0 ? "#c62828" : "#2e7d32" }}
            >
              {nonConformes}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
              {nonConformes === 0
                ? "Toutes les exigences sont couvertes"
                : `sur ${totalRefLines} dans le référentiel - sans procédure ou avec écart`}
            </MDTypography>
          </MDBox>

          {/* Conformité réglementaire - taux et son complément dans une seule barre */}
          <MDBox sx={{ ...panel, flex: "1 1 100%", borderLeft: "4px solid #0A3475" }}>
            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
              <Icon sx={{ color: "#0A3475", fontSize: "1.1rem" }}>verified</Icon>
              <MDTypography variant="caption" sx={{ color: "#6b7a99", fontWeight: 600 }}>
                Conformité réglementaire
              </MDTypography>
            </MDBox>
            <MDBox
              display="flex"
              sx={{ height: 10, borderRadius: "5px", overflow: "hidden", gap: "2px" }}
            >
              {taux > 0 && (
                <MDBox
                  sx={{
                    width: `${taux}%`,
                    bgcolor: "#2e7d32",
                    borderRadius: taux === 100 ? "5px" : "5px 0 0 5px",
                  }}
                />
              )}
              {tauxNonConf > 0 && (
                <MDBox
                  sx={{
                    width: `${tauxNonConf}%`,
                    bgcolor: "#c62828",
                    borderRadius: tauxNonConf === 100 ? "5px" : "0 5px 5px 0",
                  }}
                />
              )}
            </MDBox>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={1.2}>
              <MDBox display="flex" alignItems="center" gap={0.8}>
                <MDBox
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: "#2e7d32",
                  }}
                />
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
                  {taux}%
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                  conforme
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={0.8}>
                <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                  non conforme
                </MDTypography>
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
                  {tauxNonConf}%
                </MDTypography>
                <MDBox
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: "#c62828",
                  }}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* ── Procédures par domaine + Exigences par domaine ── */}
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={3}>
          <MDBox sx={{ ...panel, flex: "1 1 300px" }}>
            <MDTypography
              variant="button"
              fontWeight="bold"
              sx={{ color: "#0A3475", display: "block", mb: 1.5 }}
            >
              Procédures par domaine
            </MDTypography>
            {sortedDomains.length === 0 ? (
              <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                Aucune donnée disponible
              </MDTypography>
            ) : (
              sortedDomains.slice(0, 7).map(([dom, count]) => {
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <MDBox key={dom} mb={1.5}>
                    <MDBox display="flex" justifyContent="space-between" mb={0.4}>
                      <MDTypography
                        variant="caption"
                        sx={{
                          color: "#444",
                          fontWeight: 600,
                          maxWidth: "78%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {dom}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#555" }}>
                        {count}
                      </MDTypography>
                    </MDBox>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 5,
                        borderRadius: 3,
                        bgcolor: "#f0f0f0",
                        "& .MuiLinearProgress-bar": { bgcolor: "#186A81" },
                      }}
                    />
                  </MDBox>
                );
              })
            )}
          </MDBox>

          {/* Exigences par domaine */}
          {exigencesParDomaine.length > 0 && (
            <MDBox sx={{ ...panel, flex: "1 1 300px" }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon sx={{ color: "#0A3475", fontSize: "1.1rem" }}>bar_chart</Icon>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0A3475" }}>
                  Exigences par domaine
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#9e9e9e", ml: 0.5 }}>
                  - textes actifs uniquement
                </MDTypography>
                <MDBox ml="auto">
                  <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                    Total : {totalExigencesCount ?? "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>
              {[...exigencesParDomaine]
                .sort((a, b) => b.total - a.total)
                .map((row, idx, arr) => {
                  const pct = totalExigencesCount > 0 ? (row.total / totalExigencesCount) * 100 : 0;
                  const isLast = idx === arr.length - 1;
                  return (
                    <MDBox
                      key={row.domaine}
                      sx={{
                        pb: isLast ? 0 : 1.8,
                        mb: isLast ? 0 : 1.8,
                        borderBottom: isLast ? "none" : "1px solid #f0f2f5",
                      }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={0.8}
                      >
                        <MDBox display="flex" alignItems="center" gap={0.8}>
                          <Icon
                            sx={{
                              color: "#186A81",
                              fontSize: "15px !important",
                              flexShrink: 0,
                            }}
                          >
                            domain
                          </Icon>
                          <MDTypography
                            variant="caption"
                            fontWeight="medium"
                            color="dark"
                            sx={{ fontSize: "0.78rem" }}
                          >
                            {row.domaine}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: "#0A3475", fontSize: "0.8rem" }}
                          >
                            {row.total}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            sx={{ color: "#9e9e9e", fontSize: "0.72rem" }}
                          >
                            ({pct.toFixed(0)}%)
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <MDBox
                        sx={{
                          width: "100%",
                          height: 10,
                          borderRadius: "6px",
                          bgcolor: "#f0f2f5",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <MDBox
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${Math.max(pct, 1.5)}%`,
                            background:
                              "linear-gradient(90deg, #0A3475 0%, #186A81 60%, #1a9bb5 100%)",
                            borderRadius: "6px",
                            transition: "width 1s cubic-bezier(0.4,0,0.2,1)",
                          }}
                        />
                      </MDBox>
                    </MDBox>
                  );
                })}
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    );
  }

  // ── Rendu principal ───────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Card principale */}
        <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
          {/* En-tête gradient */}
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="purple"
            borderRadius="lg"
            coloredShadow="dark"
            sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
          >
            <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
              Procédures Internes
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
            >
              Gestion des procédures et du référentiel des exigences réglementaires
            </MDTypography>
          </MDBox>

          <MDBox px={2} pt={2} pb={2}>
            {/* Sélecteurs globaux */}
            {isAdminGlobal && (
              <MDBox
                mb={2}
                sx={{
                  background: "linear-gradient(135deg, #eef2f8 0%, #e8f0fb 100%)",
                  border: "1px solid #c8d8ee",
                  borderLeft: "4px solid #0A3475",
                  borderRadius: "10px",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "#0A3475", fontSize: "1.3rem" }}>admin_panel_settings</Icon>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ color: "#0A3475", display: "block", lineHeight: 1.2 }}
                    >
                      Vue Globale
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#7a8eab", fontSize: "0.68rem" }}>
                      Filtrez les données par institution
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox display="flex" gap={1.5} ml="auto" flexWrap="wrap" alignItems="center">
                  <FormControl sx={{ minWidth: 230 }}>
                    <Select
                      displayEmpty
                      value={selectedInstitution || ""}
                      onChange={(e) => setSelectedInstitution(e.target.value)}
                      sx={{ height: "45px" }}
                      renderValue={(v) =>
                        v ? (
                          institutions.find((i) => i.id === Number(v))?.nom
                        ) : (
                          <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                            Sélectionner une institution
                          </span>
                        )
                      }
                    >
                      {institutions.map((inst) => (
                        <MenuItem key={inst.id} value={inst.id}>
                          <MDBox display="flex" alignItems="center" gap={0.75}>
                            <Icon sx={{ fontSize: "1rem", color: "#0A3475" }}>account_balance</Icon>
                            <span>{inst.nom}</span>
                          </MDBox>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 200 }}>
                    <Select
                      displayEmpty
                      value={selectedDomaine}
                      onChange={(e) => setSelectedDomaine(e.target.value)}
                      sx={{ height: "45px" }}
                      renderValue={(v) =>
                        v ? (
                          domaines.find((d) => d.id === v)?.nom
                        ) : (
                          <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                            Tous les domaines
                          </span>
                        )
                      }
                    >
                      <MenuItem value="">Tous les domaines</MenuItem>
                      {domaines.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.nom}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>
              </MDBox>
            )}

            {/* Tabs */}
            {(!isAdminGlobal || selectedInstitution) && (
              <MDBox
                display="flex"
                flexWrap="wrap"
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: "#f8f9fa",
                  width: "fit-content",
                }}
              >
                {TAB_LABELS.filter((_, i) => !(isUtilisateur && i === 2)).map((t, i) => (
                  <div
                    key={i}
                    onClick={() => setActiveTab(i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 16px",
                      cursor: "pointer",
                      background:
                        activeTab === i
                          ? "linear-gradient(195deg, #0A3475, #186A81)"
                          : "transparent",
                      color: activeTab === i ? "#FFD740" : "#555555",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                      transition: "all 0.2s",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (activeTab !== i) e.currentTarget.style.backgroundColor = "#e8edf5";
                    }}
                    onMouseLeave={(e) => {
                      if (activeTab !== i) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <span>{t.label}</span>
                  </div>
                ))}
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* Contenu onglets */}
        {isAdminGlobal && !selectedInstitution ? (
          <MDBox
            mt={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
              background: "#f8f9fa",
              borderRadius: "12px",
              border: "1px dashed #c8d8ee",
            }}
          >
            <Icon sx={{ fontSize: "3rem !important", color: "#b0c4de" }}>account_balance</Icon>
            <MDTypography variant="h6" sx={{ color: "#4B5E80", fontWeight: 600 }}>
              Sélectionnez une institution
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ color: "#9e9e9e", textAlign: "center", maxWidth: 340 }}
            >
              Choisissez une institution dans le sélecteur ci-dessus pour afficher les procédures et
              le référentiel correspondants.
            </MDTypography>
          </MDBox>
        ) : (
          <Card sx={{ mt: 3 }}>
            <MDBox p={3}>
              {activeTab === 0 ? renderTabA() : activeTab === 1 ? renderTabB() : renderTabC()}
            </MDBox>
          </Card>
        )}

        {/* Dialogues */}
        {renderDialogProc()}
        {renderDialogRef()}
        {renderDialogExigences()}
        {renderDialogVersions()}
        {renderDialogViewer()}

        {/* Dialog de confirmation */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        >
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
              color="secondary"
            >
              Annuler
            </MDButton>
            <MDButton
              onClick={() => {
                setConfirmDialog({ ...confirmDialog, open: false });
                confirmDialog.onConfirm?.();
              }}
              variant="gradient"
              color="warning"
            >
              Confirmer
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snack.severity}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>

        {/* ── Modal détail procédure ── */}
        <Dialog
          fullWidth
          maxWidth="md"
          open={procedureDetailDialog.open}
          onClose={() => setProcedureDetailDialog({ open: false, proc: null })}
        >
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#0A3475" }}>
                {procedureDetailDialog.proc?.titre}
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={1}>
                {procedureDetailDialog.proc && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<Icon>print</Icon>}
                    onClick={() => {
                      const proc = procedureDetailDialog.proc;
                      const lignes = referentielAll.filter((l) => l.procedure_id === proc.id);
                      let instNom = "ConformIMF";
                      if (isAdminGlobal) {
                        if (selectedInstitution) {
                          const inst = institutions.find(
                            (i) => i.id === Number(selectedInstitution)
                          );
                          instNom = inst?.nom || `Institution #${selectedInstitution}`;
                        } else {
                          instNom = "Toutes les institutions";
                        }
                      } else {
                        instNom = user?.profile?.roles_details?.[0]?.entite?.nom || "Institution";
                      }
                      const html = buildIndicateursProcedureHtml(proc, lignes, instNom);
                      const win = window.open("", "_blank", "width=1000,height=800");
                      if (!win) {
                        showSnack(
                          "Popups bloqués par le navigateur. Autorisez-les pour ce site.",
                          "warning"
                        );
                        return;
                      }
                      win.document.write(html);
                      win.document.close();
                    }}
                  >
                    Imprimer
                  </MDButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => setProcedureDetailDialog({ open: false, proc: null })}
                >
                  <Icon>close</Icon>
                </IconButton>
              </MDBox>
            </MDBox>
          </DialogTitle>
          <DialogContent sx={{ pt: "12px !important" }}>
            {procedureDetailDialog.proc &&
              (() => {
                const proc = procedureDetailDialog.proc;
                const lignes = referentielAll.filter((l) => l.procedure_id === proc.id);
                const total = lignes.length;
                const nbOk = lignes.filter((l) => !l.ecart_procedure).length;
                const nbEcarts = lignes.filter((l) => l.ecart_procedure).length;
                const tauxConf = total > 0 ? Math.round((nbOk / total) * 100) : 0;
                const tauxNonConf = total > 0 ? Math.round((nbEcarts / total) * 100) : 0;

                const kpi = (val, label, color) => (
                  <MDBox
                    flex={1}
                    textAlign="center"
                    p={1.5}
                    sx={{ background: "#f8f9fa", borderRadius: 2, border: `2px solid ${color}22` }}
                  >
                    <MDTypography variant="h4" fontWeight="bold" sx={{ color }}>
                      {val}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      sx={{ color: "#666", lineHeight: 1.3, display: "block", mt: 0.3 }}
                    >
                      {label}
                    </MDTypography>
                  </MDBox>
                );

                return (
                  <MDBox>
                    <MDBox display="flex" flexWrap="wrap" gap={2} mb={2.5}>
                      <Chip
                        label={
                          proc.statut === "ACTIF"
                            ? "Actif"
                            : proc.statut === "EN_VALIDATION"
                            ? "En validation"
                            : proc.statut === "BROUILLON"
                            ? "Brouillon"
                            : "Archivé"
                        }
                        size="small"
                        sx={{
                          bgcolor: proc.statut === "ACTIF" ? "#e8f5e9" : "#fff3e0",
                          color: proc.statut === "ACTIF" ? "#2e7d32" : "#e65100",
                          fontWeight: 600,
                        }}
                      />
                      {proc.domaine?.nom && (
                        <Chip
                          label={proc.domaine.nom}
                          size="small"
                          sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
                        />
                      )}
                      {proc.date_adoption && (
                        <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                          Adoptée le {new Date(proc.date_adoption).toLocaleDateString("fr-FR")}
                        </MDTypography>
                      )}
                    </MDBox>

                    {/* ── 5 indicateurs ── */}
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      sx={{ color: "#6a1b9a", display: "block", mb: 1.5 }}
                    >
                      Indicateurs de conformité
                    </MDTypography>
                    <MDBox display="flex" gap={1.5} flexWrap="wrap" mb={2.5}>
                      {kpi(total, "Total exigences réglementaires liées", "#0A3475")}
                      {kpi(nbOk, "Exigences couvertes", "#2e7d32")}
                      {kpi(
                        nbEcarts,
                        "Exigences non couvertes",
                        nbEcarts > 0 ? "#c62828" : "#9e9e9e"
                      )}
                    </MDBox>
                    <MDBox display="flex" gap={1.5} flexWrap="wrap" mb={2.5}>
                      <MDBox
                        flex={1}
                        p={1.5}
                        sx={{
                          background: "#f0faf0",
                          borderRadius: 2,
                          border: "2px solid #2e7d3222",
                        }}
                      >
                        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                          {tauxConf}%
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{ color: "#666", display: "block", mb: 0.8 }}
                        >
                          Taux de conformité
                        </MDTypography>
                        <LinearProgress
                          variant="determinate"
                          value={tauxConf}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "#e0e0e0",
                            "& .MuiLinearProgress-bar": { bgcolor: "#2e7d32" },
                          }}
                        />
                      </MDBox>
                      <MDBox
                        flex={1}
                        p={1.5}
                        sx={{
                          background: "#fff8f8",
                          borderRadius: 2,
                          border: "2px solid #c6282822",
                        }}
                      >
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          sx={{ color: tauxNonConf > 0 ? "#c62828" : "#9e9e9e" }}
                        >
                          {tauxNonConf}%
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          sx={{ color: "#666", display: "block", mb: 0.8 }}
                        >
                          Taux de non-conformité
                        </MDTypography>
                        <LinearProgress
                          variant="determinate"
                          value={tauxNonConf}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "#e0e0e0",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: tauxNonConf > 0 ? "#c62828" : "#9e9e9e",
                            },
                          }}
                        />
                      </MDBox>
                    </MDBox>
                    {lignes.length > 0 && (
                      <>
                        <MDTypography
                          variant="button"
                          fontWeight="bold"
                          sx={{ color: "#0A3475", display: "block", mb: 1 }}
                        >
                          Exigences réglementaires liées
                        </MDTypography>
                        {lignes.map((l) => (
                          <MDBox
                            key={l.id}
                            display="flex"
                            alignItems="flex-start"
                            gap={1}
                            py={0.8}
                            sx={{ borderBottom: "1px solid #f0f2f5" }}
                          >
                            <Icon
                              sx={{
                                fontSize: "14px",
                                color: l.ecart_procedure ? "#c62828" : "#2e7d32",
                                mt: 0.2,
                              }}
                            >
                              {l.ecart_procedure ? "cancel" : "check_circle"}
                            </Icon>
                            <MDBox flex={1}>
                              <MDTypography
                                variant="caption"
                                sx={{ display: "block", lineHeight: 1.4 }}
                              >
                                {l.exigence?.exigence || "-"}
                              </MDTypography>
                              {(l.texte_titre || l.exigence?.reference) && (
                                <MDTypography
                                  variant="caption"
                                  sx={{ color: "#9e9e9e", fontSize: "0.68rem" }}
                                >
                                  {[l.texte_titre, l.exigence?.reference]
                                    .filter(Boolean)
                                    .join(" - ")}
                                </MDTypography>
                              )}
                              {l.observations && (
                                <MDTypography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "#6a1b9a",
                                    fontSize: "0.68rem",
                                    fontStyle: "italic",
                                    mt: 0.3,
                                  }}
                                >
                                  Obs. : {l.observations}
                                </MDTypography>
                              )}
                            </MDBox>
                            {l.ecart_procedure && (
                              <Chip
                                label="Écart"
                                size="small"
                                sx={{
                                  fontSize: "0.6rem",
                                  height: 18,
                                  bgcolor: "#fdecea",
                                  color: "#c62828",
                                }}
                              />
                            )}
                          </MDBox>
                        ))}
                      </>
                    )}
                  </MDBox>
                );
              })()}
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="text"
              color="secondary"
              onClick={() => setProcedureDetailDialog({ open: false, proc: null })}
            >
              Fermer
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
